import { filters as filterSkeletons, TABLE_OPTIONS_KEYS } from "@/components/ModernFilterBar/filters";
import { Router } from "@/composables/Router";
import { deepClone } from "@/helpers/helpers";
import { convertQueryStringToParamsObject, getFullQuery } from "../components/NewFilterBar/FilterBar";

export default class UrlAdapter {
    constructor(){
        this.router = Router()
        this.beforeUrlChangeCallbacks = []
        this.getStateCallbacks = []
    }

    async loadDefaultBookmark(bookmarkType, bookmarks) {
        if (!bookmarkType) {
            return
        }

        if (Object.keys(this.router.currentRoute.query).length && !(Object.keys(this.router.currentRoute.query).length === 1 && Object.keys(this.router.currentRoute.query)[0] === 'dates_list')) {
            // If any params are set then dont load the default bookmark
            // (except if only dates_list is set in dashboard)
            // This is a bit hacky yes, but I don't see a good way to solve this
            // The dates_list will be set before we get to this method
            return
        }

        const defaultBookmarksJson = localStorage.getItem('defaultBookmarks')
        if (!defaultBookmarksJson) {
            return
        }

        let defaultBookmarks = {}

        try {
            defaultBookmarks = JSON.parse(defaultBookmarksJson)
        } catch (e) {
            console.error(e)
            return
        }

        const defaultBookmarkId = defaultBookmarks[bookmarkType]
        if (!defaultBookmarkId) {
            return
        }

        const bookmark = bookmarks.find(x => x.id === defaultBookmarkId)
        if (!bookmark) {
            return
        }

        let new_link = bookmark.link.replace(/^\/app/, "");
        new_link = new_link.replaceAll('active_bookmark_id', 'x');
        new_link += "&active_bookmark_id=" + bookmark.id

        await this.router.push(new_link)
    }

    setOnUrlChange(onUrlChange){
        this.beforeUrlChangeCallbacks.push(onUrlChange)
    }

    addGetStateCallback(callback) {
        this.getStateCallbacks.push(callback)
    }

    async updateUrlParameters(disableLoadData) {
        let params = {}

        for (const callback of this.getStateCallbacks) {
            const res = callback()
            params = { ...params, ...res }
        }

        return await this.setUrlParametersFromObject(params, true, disableLoadData)
    }

    async setUrlParametersFromObject(params_object, overwrite, disableLoadData){
        let params = this.convertQueryStringToParamsObject(this.getFullQuery()) || {}
        const urlParamsBackup = {...params}
        if (overwrite) params = {}

        if (params_object) {
            for (let _key of Object.keys(params_object)) {
                // If params_object.columns list is completely empty, then just ignore it and keep the URL columns value instead.
                // This helps ensure that RichDataTableController.initAsync works!
                if (_key === "columns" && params_object[_key] && params_object[_key].length === 0) {
                    params[_key] = urlParamsBackup[_key] || []
                } else {
                    params[_key] = params_object[_key]
                }
            }
        }
        return await this._actuallySetUrlFromObject(params, disableLoadData)
    }
    async setUrlParameter(key, value){
        let params = this.convertQueryStringToParamsObject(this.getFullQuery())
        if (params) {
            params[key] = value
            return await this._actuallySetUrlFromObject(params)
        }
    }
    getUrlParameter(key){
        let params = this.convertQueryStringToParamsObject(this.getFullQuery())
        if (params) {
            return params[key]
        }
    }
    getUrlParameters() {
        const fullQuery = this.getFullQuery()
        return this.convertQueryStringToParamsObject(fullQuery)
    }
    async deleteUrlParameter(key){
        let params = this.convertQueryStringToParamsObject(this.getFullQuery())
        if (params) {
            delete params[key]
            await this._actuallySetUrlFromObject(params)
        }
    }

    async _actuallySetUrlFromObject(params, disableLoad){
        if (this.convertParamsObjectToQueryString(params)){
            for (const callback of this.beforeUrlChangeCallbacks) {
                await callback(params, disableLoad)

            }

            this._pushRoute({ query: deepClone(params) })
        }
        return params
    }
    _pushRoute(query){
        this.router.push(query).catch(() => {});
    }
    getFullPath(){
        return this.router.currentRoute.fullPath
    }
    getFullQuery(){
        /**
         * Get the full query
         */

        const fullPath = this.getFullPath()
        let answer = ""
        const result = fullPath && fullPath.length ? fullPath.split("?") : []
        if (result.length >= 2) answer = result[1]
        return answer
    }
    convertParamsObjectToQueryString(params, leading_string = '?'){
        /**
         * This function holds the knowledge of how to convert the parameters object into a query string.
         * This is a reverse function of convertQueryStringToParamsObject.
         */

        let str = leading_string;

        Object.entries(params).map((i, index, arr) => {
            str += i[0];
            str += "=";
            const matchingFilterObject = filterSkeletons.find(ii => ii.key === i[0]);
            if (matchingFilterObject && matchingFilterObject.multi && i[1] && Array.isArray(i[1])) {
                str += i[1].join(matchingFilterObject.delimiter || "~");
            } else {
                str += i[1];
            }

            if (index !== arr.length - 1) {
                str += "&";
            }
        });
        // Tornado treats ; as a reserved symbol and will do magic if not escaped
        str = str.replace(';', '%3B').replace('+', '%2B');
        if (str === '?') return ''
        return str;
    }
    convertQueryStringToParamsObject(qs){

        /**
         * This function holds the knowledge of how to convert the query string into a parameters object.
         * This is a reverse function of convertParamsObjectToQueryString but holds more logic since we need to infer types.
         */

        let qq = qs.split("&").reduce((acc, curr) => {
            const pair = curr.split("=");
            if (pair.length < 2) return acc
            const key = pair[0];
            let val = pair[1];
            const matchingFilterObject = filterSkeletons.find(i => i.key === key);

            if (key) {
                if (matchingFilterObject) {
                    /**
                     *  Handle filter related values - will always return an array
                     */

                    val = decodeURIComponent(val)

                    if (val.length) {
                        acc[key] = val.split(matchingFilterObject.delimiter || ",").map(i => {
                            if (matchingFilterObject.int) {
                                return parseInt(i);
                            } else {
                                return decodeURIComponent(i);
                            }
                        });
                    } else {
                        acc[key] = [];
                    }
                }
                else if (TABLE_OPTIONS_KEYS.includes(key)) {
                    /**
                     *  Key is a v-data-table options key
                     *  v-data-table is strict on types and will go nuts if types are wrong.
                     */

                    if (["page", "itemsPerPage"].includes(key)) {
                        acc[key] = parseInt(val);
                    } else if (["sortDesc", "mustSort", "multiSort"].includes(key)) {
                        acc[key] = val === "true";
                    } else if (["sortBy"].includes(key)) {
                        acc[key] = val;
                    }
                    // fixme :: are all "use cases" handled above ?

                }
                else if (val.includes(',')) {
                    /**
                     * Value is probably a non-filter csv
                     */

                    const arr = val.split(",");

                    if (arr.length === 1 && arr[0] === "") {
                        acc[key] = [];
                    } else {
                        acc[key] = arr.map(i => decodeURIComponent(i));
                    }

                } else {
                    /**
                     * Value is probably a boolean
                     */

                    if (['true', 'false'].includes(val)) {
                        acc[key] = val === 'true';
                    } else {
                        acc[key] = decodeURIComponent(val);
                    }
                }
            }

            return acc;
        }, {});
        return qq
    }
}


